import type {
  EffectivePaper, Layout, MarginsConfig, PaperConfig, SignConfig, TileConfig, ViewMode,
} from "./types";

export const computePages = (
  paperW: number,
  paperH: number,
  margins: MarginsConfig,
  sign: SignConfig,
): Layout => {
  const availW = Math.max(0.1, paperW - margins.left - margins.right);
  const availH = Math.max(0.1, paperH - margins.top - margins.bottom);
  const cols = Math.max(1, Math.ceil(sign.width / availW));
  const rows = Math.max(1, Math.ceil(sign.height / availH));
  return { paper: { width: paperW, height: paperH }, cols, rows, availW, availH };
};

export const effectivePaper = (
  paper: PaperConfig,
  margins: MarginsConfig,
  sign: SignConfig,
): EffectivePaper => {
  const { width, height, orientation } = paper;
  if (orientation === "auto") {
    const portrait = computePages(width, height, margins, sign);
    const landscape = computePages(height, width, margins, sign);
    if (landscape.cols * landscape.rows < portrait.cols * portrait.rows) {
      return { width: height, height: width };
    }
    return { width, height };
  }
  if (orientation === "landscape" && width < height) return { width: height, height: width };
  if (orientation === "portrait"  && width > height) return { width: height, height: width };
  return { width, height };
};

export const buildLayout = (
  paper: PaperConfig,
  margins: MarginsConfig,
  sign: SignConfig,
  tile: TileConfig,
): Layout => {
  const ep = effectivePaper(paper, margins, sign);
  if (tile.auto) {
    return computePages(ep.width, ep.height, margins, sign);
  }
  return {
    paper: ep,
    cols: tile.cols,
    rows: tile.rows,
    availW: Math.max(0.1, ep.width - margins.left - margins.right),
    availH: Math.max(0.1, ep.height - margins.top - margins.bottom),
  };
};

export const printGapMm = (paper: EffectivePaper): number =>
  Math.max(2, Math.min(paper.width, paper.height) * 0.03);

export const contentSize = (
  layout: Layout,
  view: ViewMode,
  sign: SignConfig,
): { w: number; h: number } => {
  if (view === "print") {
    const gap = printGapMm(layout.paper);
    return {
      w: layout.cols * layout.paper.width  + (layout.cols - 1) * gap,
      h: layout.rows * layout.paper.height + (layout.rows - 1) * gap,
    };
  }
  return { w: sign.width, h: sign.height };
};

export const computeFitScale = (
  hostW: number,
  hostH: number,
  contentW: number,
  contentH: number,
  padding = 24,
): number => {
  const cw = hostW - padding * 2;
  const ch = hostH - padding * 2;
  return Math.max(0.001, Math.min(cw / contentW, ch / contentH));
};
